import {
  type FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Link } from 'react-router';

import type { ContactInquiryRequest } from '../api/generated/models/ContactInquiryRequest';
import type { ContactPublicReadiness } from '../api/generated/models/ContactPublicReadiness';
import { useGuidedMode } from '../guidance/guidedModeState';
import {
  PublicContactError,
  publicContactClient,
  type PublicContactClient,
} from './contactClient';
import {
  TurnstileChallenge,
} from './TurnstileChallenge';
import type { TurnstileLoader } from './turnstile';
import { guided } from '../guidance/fieldHelpRegistryCore';
import { contactFieldHelp } from '../guidance/fieldHelpRegistry';

const MINIMUM_FORM_DWELL_MS = 3_000;
const GENERIC_RETRY_MESSAGE =
  'We could not submit your request. Your message is still here. Complete a fresh verification and try again.';
const MARKUP_CHARACTER = /[<>]/u;
const EMAIL_FORMAT = /^[^\s@]+@[^\s@]+\.[^\s@]+$/u;

interface ContactDraft {
  consent: boolean;
  email: string;
  message: string;
  name: string;
  organization: string;
  website: string;
}

type ContactField = 'email' | 'message' | 'name' | 'organization';
type FieldErrors = Partial<Record<ContactField | 'consent', string>>;

interface CanonicalBusinessFields {
  email: string;
  message: string;
  name: string;
  organization: string | null;
}

interface LogicalAttempt {
  fingerprint: string;
  idempotencyKey: string;
}

interface ContactFormProps {
  client?: PublicContactClient;
  now?: (() => number) | undefined;
  privacyNoticeVersion: string;
  turnstileLoader?: TurnstileLoader | undefined;
  turnstileSiteKey: string;
  uuid?: (() => string) | undefined;
}

interface ContactExperienceProps {
  client?: PublicContactClient;
  now?: (() => number) | undefined;
  turnstileLoader?: TurnstileLoader | undefined;
  uuid?: (() => string) | undefined;
}

const EMPTY_DRAFT: ContactDraft = {
  consent: false,
  email: '',
  message: '',
  name: '',
  organization: '',
  website: '',
};

function normalizeSingleLine(value: string): string {
  return value.normalize('NFC').trim().replace(/\s+/gu, ' ');
}

function normalizeEmail(value: string): string {
  return value.normalize('NFKC').trim().toLocaleLowerCase('en-US');
}

function normalizeMessage(value: string): string {
  return value.normalize('NFC').replace(/\r\n?/gu, '\n').trim();
}

function canonicalizeDraft(draft: ContactDraft): CanonicalBusinessFields {
  const organization = normalizeSingleLine(draft.organization);
  return {
    email: normalizeEmail(draft.email),
    message: normalizeMessage(draft.message),
    name: normalizeSingleLine(draft.name),
    organization: organization.length === 0 ? null : organization,
  };
}

function submissionFingerprint(
  fields: CanonicalBusinessFields,
  privacyNoticeVersion: string,
): string {
  return JSON.stringify({ ...fields, privacyNoticeVersion });
}

function hasUnsafeText(value: string): boolean {
  for (const character of value) {
    const codePoint = character.codePointAt(0);
    if (
      codePoint !== undefined &&
      ((codePoint < 32 && codePoint !== 9 && codePoint !== 10) ||
        codePoint === 127)
    ) {
      return true;
    }
  }
  return MARKUP_CHARACTER.test(value);
}

function validateDraft(
  fields: CanonicalBusinessFields,
  consent: boolean,
): FieldErrors {
  const errors: FieldErrors = {};
  if (fields.name.length === 0) errors.name = 'Enter your name.';
  else if (fields.name.length > 120)
    errors.name = 'Use 120 characters or fewer.';
  else if (hasUnsafeText(fields.name))
    errors.name = 'Use plain text without markup or control characters.';

  if (fields.email.length === 0) errors.email = 'Enter your email address.';
  else if (fields.email.length > 254)
    errors.email = 'Use 254 characters or fewer.';
  else if (
    !EMAIL_FORMAT.test(fields.email) ||
    hasUnsafeText(fields.email)
  )
    errors.email = 'Enter a valid email address.';

  if (fields.organization !== null) {
    if (fields.organization.length > 160)
      errors.organization = 'Use 160 characters or fewer.';
    else if (hasUnsafeText(fields.organization))
      errors.organization =
        'Use plain text without markup or control characters.';
  }

  if (fields.message.length < 10)
    errors.message = 'Enter at least 10 characters.';
  else if (fields.message.length > 4_000)
    errors.message = 'Use 4,000 characters or fewer.';
  else if (hasUnsafeText(fields.message))
    errors.message = 'Use plain text without markup or control characters.';

  if (!consent)
    errors.consent = 'Consent to the current privacy notice is required.';
  return errors;
}

function focusFirstError(errors: FieldErrors) {
  const order: Array<keyof FieldErrors> = [
    'name',
    'email',
    'organization',
    'message',
    'consent',
  ];
  const first = order.find((field) => errors[field] !== undefined);
  if (first !== undefined) {
    document.getElementById(`contact-${first}`)?.focus();
  }
}

function FieldError({ id, message }: { id: string; message: string | undefined }) {
  if (message === undefined) return null;
  return (
    <span className="contact-field__error" id={id}>
      {message}
    </span>
  );
}

function withoutFieldError(
  current: FieldErrors,
  field: keyof FieldErrors,
): FieldErrors {
  const next = { ...current };
  delete next[field];
  return next;
}

export function ContactForm({
  client = publicContactClient,
  now = Date.now,
  privacyNoticeVersion,
  turnstileLoader,
  turnstileSiteKey,
  uuid = () => crypto.randomUUID(),
}: ContactFormProps) {
  const { enabled: guidedEnabled, setEnabled: setGuidedEnabled } = useGuidedMode();
  const [challengeError, setChallengeError] = useState(false);
  const [challengeMountKey, setChallengeMountKey] = useState(0);
  const [challengeResetKey, setChallengeResetKey] = useState(0);
  const [challengeToken, setChallengeToken] = useState<string | null>(null);
  const [draft, setDraft] = useState<ContactDraft>(EMPTY_DRAFT);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [phase, setPhase] = useState<
    'idle' | 'pending' | 'error' | 'success'
  >('idle');
  const [reference, setReference] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const attemptRef = useRef<LogicalAttempt | null>(null);
  const errorRef = useRef<HTMLDivElement | null>(null);
  const startedAtRef = useRef(now());
  const successRef = useRef<HTMLDivElement | null>(null);

  useEffect(
    () => () => {
      abortRef.current?.abort();
      abortRef.current = null;
    },
    [],
  );

  useEffect(() => {
    if (phase === 'error') errorRef.current?.focus();
    if (phase === 'success') successRef.current?.focus();
  }, [phase]);

  const setField = useCallback(
    (field: ContactField, value: string) => {
      setDraft((current) => ({ ...current, [field]: value }));
      setErrors((current) => withoutFieldError(current, field));
    },
    [],
  );

  const handleToken = useCallback((token: string | null) => {
    setChallengeToken(token);
    if (token !== null) setChallengeError(false);
  }, []);

  const handleChallengeError = useCallback(() => {
    setChallengeToken(null);
    setChallengeError(true);
  }, []);

  const resetChallenge = useCallback(() => {
    setChallengeToken(null);
    setChallengeResetKey((current) => current + 1);
  }, []);

  const retryChallenge = useCallback(() => {
    setChallengeError(false);
    setChallengeToken(null);
    setChallengeMountKey((current) => current + 1);
  }, []);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (phase === 'pending') return;

    const canonical = canonicalizeDraft(draft);
    const nextErrors = validateDraft(canonical, draft.consent);
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      setPhase('idle');
      window.setTimeout(() => focusFirstError(nextErrors), 0);
      return;
    }

    if (
      draft.website.length > 0 ||
      now() - startedAtRef.current < MINIMUM_FORM_DWELL_MS ||
      challengeToken === null
    ) {
      setPhase('error');
      resetChallenge();
      return;
    }

    const fingerprint = submissionFingerprint(canonical, privacyNoticeVersion);
    const previousAttempt = attemptRef.current;
    const logicalAttempt =
      previousAttempt !== null && previousAttempt.fingerprint === fingerprint
        ? previousAttempt
        : { fingerprint, idempotencyKey: uuid() };
    attemptRef.current = logicalAttempt;

    const request: ContactInquiryRequest = {
      challenge_token: challengeToken,
      email: canonical.email,
      idempotency_key: logicalAttempt.idempotencyKey,
      message: canonical.message,
      name: canonical.name,
      organization: canonical.organization,
      privacy_notice_version: privacyNoticeVersion,
    };
    const controller = new AbortController();
    abortRef.current = controller;
    setErrors({});
    setPhase('pending');

    try {
      const accepted = await client.submit(request, controller.signal);
      setDraft(EMPTY_DRAFT);
      setReference(accepted.reference);
      setPhase('success');
      attemptRef.current = null;
    } catch (error) {
      if (
        error instanceof PublicContactError &&
        error.requiresNewIdempotencyKey
      ) {
        attemptRef.current = null;
      }
      if (!controller.signal.aborted) setPhase('error');
    } finally {
      if (abortRef.current === controller) abortRef.current = null;
      resetChallenge();
    }
  };

  const buttonLabel = useMemo(() => {
    if (phase === 'pending') return 'Sending…';
    if (phase === 'error') return 'Retry request';
    return 'Send inquiry';
  }, [phase]);

  if (phase === 'success' && reference !== null) {
    return (
      <div
        className="contact-success"
        ref={successRef}
        role="status"
        tabIndex={-1}
      >
        <p className="eyebrow">Request received</p>
        <h3>Thanks for contacting Flow Wizard.</h3>
        <p>
          We received your request as <strong>{reference}</strong>. Jesse will
          review it and reply to the address you provided. This automatic
          message confirms receipt only; it is not an engineering
          recommendation.
        </p>
        <button
          className="button button--secondary"
          onClick={() => {
            startedAtRef.current = now();
            setReference(null);
            setPhase('idle');
          }}
          type="button"
        >
          Send another inquiry
        </button>
      </div>
    );
  }

  const describedBy = (field: ContactField) =>
    errors[field] === undefined ? undefined : `contact-${field}-error`;

  return (
    <form
      aria-busy={phase === 'pending'}
      autoComplete="off"
      className="contact-form"
      noValidate
      onSubmit={(event) => void submit(event)}
    >
      <div className="contact-form__heading">
        <h3>Send a message</h3>
        <span>Required fields are marked.</span>
      </div>

      <div className="contact-guided-control">
        <button
          aria-checked={guidedEnabled}
          className="contact-guided-control__switch"
          onClick={() => setGuidedEnabled(!guidedEnabled)}
          role="switch"
          type="button"
        >
          <span>Field help</span>
          <strong>{guidedEnabled ? 'On' : 'Off'}</strong>
        </button>
      </div>

      {phase === 'error' ? (
        <div
          className="contact-form__error"
          ref={errorRef}
          role="alert"
          tabIndex={-1}
        >
          {GENERIC_RETRY_MESSAGE}
        </div>
      ) : (
        <div aria-live="polite" className="contact-form__live">
          {phase === 'pending' ? 'Submitting your inquiry…' : ''}
        </div>
      )}

      <div className="contact-form__grid">
        <label className="contact-field" htmlFor="contact-name">
          <span>Name <em>Required</em></span>
          <input {...guided(contactFieldHelp.name)}
            aria-describedby={describedBy('name')}
            aria-invalid={errors.name !== undefined}
            autoComplete="off"
            id="contact-name"
            maxLength={120}
            onChange={(event) => setField('name', event.currentTarget.value)}
            required
            type="text"
            value={draft.name}
          />
          <FieldError id="contact-name-error" message={errors.name} />
        </label>
        <label className="contact-field" htmlFor="contact-email">
          <span>Email <em>Required</em></span>
          <input {...guided(contactFieldHelp.email)}
            aria-describedby={describedBy('email')}
            aria-invalid={errors.email !== undefined}
            autoComplete="off"
            id="contact-email"
            maxLength={254}
            onChange={(event) => setField('email', event.currentTarget.value)}
            required
            type="email"
            value={draft.email}
          />
          <FieldError id="contact-email-error" message={errors.email} />
        </label>
      </div>

      <label className="contact-field" htmlFor="contact-organization">
        <span>Organization <small>Optional</small></span>
        <input {...guided(contactFieldHelp.organization)}
          aria-describedby={describedBy('organization')}
          aria-invalid={errors.organization !== undefined}
          autoComplete="off"
          id="contact-organization"
          maxLength={160}
          onChange={(event) =>
            setField('organization', event.currentTarget.value)
          }
          type="text"
          value={draft.organization}
        />
        <FieldError
          id="contact-organization-error"
          message={errors.organization}
        />
      </label>

      <label className="contact-field" htmlFor="contact-message">
        <span>How can Flow Wizard help? <em>Required</em></span>
        <textarea {...guided(contactFieldHelp.message)}
          aria-describedby={
            errors.message === undefined
              ? 'contact-message-count'
              : 'contact-message-count contact-message-error'
          }
          aria-invalid={errors.message !== undefined}
          id="contact-message"
          maxLength={4_000}
          onChange={(event) => setField('message', event.currentTarget.value)}
          required
          rows={6}
          value={draft.message}
        />
        <span className="contact-field__count" id="contact-message-count">
          {draft.message.length.toLocaleString()} / 4,000 characters
        </span>
        <FieldError id="contact-message-error" message={errors.message} />
      </label>

      <div aria-hidden="true" className="contact-honeypot">
        <label htmlFor="contact-website">Leave this field empty</label>
        <input data-guided-exempt="hidden-contact-honeypot"
          autoComplete="off"
          id="contact-website"
          name="website"
          onChange={(event) =>
            {
              const website = event.currentTarget.value;
              setDraft((current) => ({ ...current, website }));
            }
          }
          tabIndex={-1}
          type="text"
          value={draft.website}
        />
      </div>

      <label className="contact-consent" htmlFor="contact-consent">
        <input {...guided(contactFieldHelp.consent)}
          aria-describedby={
            errors.consent === undefined ? undefined : 'contact-consent-error'
          }
          aria-invalid={errors.consent !== undefined}
          checked={draft.consent}
          id="contact-consent"
          onChange={(event) => {
            const consent = event.currentTarget.checked;
            setDraft((current) => ({
              ...current,
              consent,
            }));
            setErrors((current) => withoutFieldError(current, 'consent'));
          }}
          type="checkbox"
        />
        <span>
          I have read the <Link to="/privacy">Privacy notice</Link> (version{' '}
          {privacyNoticeVersion}) and consent to processing this inquiry,
          including Cloudflare verification and Brevo email delivery.{' '}
          <em>Required</em>
          <FieldError id="contact-consent-error" message={errors.consent} />
        </span>
      </label>

      <TurnstileChallenge
        key={challengeMountKey}
        loader={turnstileLoader}
        onError={handleChallengeError}
        onToken={handleToken}
        resetKey={challengeResetKey}
        siteKey={turnstileSiteKey}
      />
      {challengeError ? (
        <div className="contact-form__challenge-error" role="alert">
          Human verification could not load. Check your connection and{' '}
          <button onClick={retryChallenge} type="button">
            retry verification
          </button>
          .
        </div>
      ) : null}

      <div className="contact-form__submit">
        <button
          className="button button--primary"
          disabled={
            phase === 'pending' || challengeToken === null || !draft.consent
          }
          type="submit"
        >
          {buttonLabel}
        </button>
        <p>
          A receipt confirms delivery only. It is not an engineering review or
          a promise of response time.
        </p>
      </div>
    </form>
  );
}

function ContactUnavailable({ checked }: { checked: boolean }) {
  return (
    <div className="contact-unavailable" role={checked ? 'status' : undefined}>
      <p className="eyebrow">Contact readiness</p>
      <h3>
        {checked
          ? 'The public contact form is not available yet.'
          : 'Checking contact availability…'}
      </h3>
      <p>
        {checked
          ? 'Flow Wizard queues no inquiry or email until its anti-bot and mail-delivery gates are configured. If you already have access, use the sign-in path.'
          : 'The form will open only when its privacy, human-verification, and delivery configuration is ready.'}
      </p>
      {checked ? (
        <Link className="button button--secondary" to="/login">
          Sign in
        </Link>
      ) : null}
    </div>
  );
}

export function ContactExperience({
  client = publicContactClient,
  now,
  turnstileLoader,
  uuid,
}: ContactExperienceProps) {
  const [readiness, setReadiness] = useState<ContactPublicReadiness | null>(null);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    void client
      .loadReadiness(controller.signal)
      .then((contact) => setReadiness(contact))
      .catch(() => setReadiness(null))
      .finally(() => {
        if (!controller.signal.aborted) setChecked(true);
      });
    return () => controller.abort();
  }, [client]);

  if (
    !checked ||
    readiness?.status !== 'ready' ||
    readiness.privacy_notice_version === null ||
    readiness.privacy_notice_version === undefined ||
    readiness.turnstile_site_key === null ||
    readiness.turnstile_site_key === undefined
  ) {
    return <ContactUnavailable checked={checked} />;
  }

  return (
    <ContactForm
      client={client}
      now={now}
      privacyNoticeVersion={readiness.privacy_notice_version}
      turnstileLoader={turnstileLoader}
      turnstileSiteKey={readiness.turnstile_site_key}
      uuid={uuid}
    />
  );
}
